<template>
    <div v-if="active" class="row my-4 mx-0">
        <div class="col-12 px-0 mb-3 mb-lg-0">
            <PageHeader :title="screenData.text.pageTitle" />
        </div>
        <Error ref="error" class="mb-3"></Error>
        <div class="col-12 p-0">
            <p>{{screenData.text.updateInfo}}</p>
        </div>
        <EmailAddress ref="email"
                      :title="screenData.text.emailTitle" 
                      :email-label="screenData.text.emailLabel"
                      :invalid-email="screenData.text.emailAddressInvalid"
                      :confirm-label="screenData.text.confirmEmailLabel"
                      v-model:email="consentData.customerEmailAddress"
                      v-model:confirm-email="consentData.customerEmailAddress"
                      :match-error="screenData.text.doNotMatch"
                      @update-email-address="updateEmailAddress"></EmailAddress>
        <ConsentOptions ref="consent"
                        :title="screenData.text.consentTitle"
                        :all-email="screenData.text.allEmail"
                        :email="screenData.text.email"
                        :all-mail="screenData.text.allMail"
                        :mail="screenData.text.mail"
                        :column-data="screenData.consentColumns"
                        v-model:options="consentData.consentInfo"
                        @update-consent="updateConsentInfo"></ConsentOptions>
        <ConsentTerms v-if="anyEmail"
                      :title="screenData.text.termsTitle"
                      :please-note="screenData.text.pleaseNote"
                      :accept-text="screenData.text.acceptingBelow"
                      :terms-list="screenData.text.acceptList"
                      :user-accepts="screenData.text.userAccepts"
                      @update-terms-agreed="updateTermsAggreed"></ConsentTerms>
        <div class="col-12 px-0 mt-4">
            <ActionList :actions="screenData.actions" @update-consent="updateConsent" @cancel-consent="cancel"></ActionList>
        </div>
    </div>
</template>

<script>
    import { nextTick } from 'vue'
    import PageHeader from '../entities/PageHeader.vue';
    import Error from '../entities/Error.vue';
    import EmailAddress from '../entities/EmailAddress.vue';
    import ConsentOptions from '../entities/ConsentOptions.vue';
    import ConsentTerms from '../entities/ConsentTerms.vue';
    import ActionList from '../entities/ActionList.vue';
    import {PostAuthHandler} from '../mixins/PostAuthHandler.js';

    export default {
        name: "ConsentInformation",
        mixins: [PostAuthHandler],

        components: {
            PageHeader,
            Error,
            EmailAddress,
            ConsentOptions,
            ConsentTerms,
            ActionList
        },

        props: {
            screenData: Object
        },

        data: function() {
            return {
                consentData: null,
                active: false,
                anyEmail: false,
                termsAggreed: false,
                consentInfoChanged: false,
                emailChanged: false,
                originalEmail: "",
                originalConsentInfo: [],
                emailInError: false,
                isPreAuth: false,
            }
        },

        methods: {
            show: function({isPreAuth = false, responseData = null, hasMoreWork = false, action = null} = {}) {
                this.active = true;
                this.isPreAuth = isPreAuth;
                if (this.isPreAuth) {
                    this.action = action;
                    this.hasMoreWork = hasMoreWork;
                    this.setResponseData(responseData);
                    this.$emitter.emit("set-full-width-layout");
                }
                this.$emitter.emit('setReturnAction', this.screenData.return);
                nextTick(function() {
                    this.$refs["consent"].init();
                    this.enableUpdateButton(false);
                }.bind(this));
            },

            hide: function() {
                this.active = false;
                this.emailChanged = false;
                this.consentInfoChanged = false;
                this.termsAggreed = false;
                this.anyEmail = false;

                if (this.isPreAuth) {
                    this.$emitter.emit("set-default-layout");
                }
            },

            updateTermsAggreed: function(val) {
                this.termsAggreed = val;
            },

            updateConsentInfo: function(consentInfo) {
                this.consentData.consentInfo = consentInfo;
            },

            updateEmailAddress: function(val) {
                if(val) this.consentData.customerEmailAddress = val;
                this.emailInError = !val;
            },

            enableUpdateButton: function(enable) {
                if (enable && (!this.anyEmail || this.anyEmail && this.hasEmailAddress)) {
                    if (this.anyChanged && !this.emailInError) {
                        this.$emitter.emit('enable','update-consent');
                    }
                }
                else {
                    this.$emitter.emit('disable','update-consent');
                }
            },

            updateConsent: function() {
                this.$emitter.emit('start-loading','update-consent');
                this.$refs["error"].clear();
                if(this.isPreAuth){
                    this.continue(this.consentData)
                }
                else{
                    var API = "/BFWeb/cwapi/" + this.getClient() + "/comweb/consent";
                    this.$axios.post(API, this.consentData).then(function(response) {
                    if (response.data.success) {
                        this.fetchData();
                        if (this.allChanged) {
                            this.$emitter.emit('display-update-consent-all-success');
                        }
                        else {
                            if (this.emailChanged) {
                                this.$emitter.emit('display-update-email-success');
                            }
                            else {
                                this.$emitter.emit('display-update-consent-option-success');
                            }
                        }
                        this.$emitter.emit('navigate','profileSettings')
                    }   
                    else {
                        var error = response.data.messages[0];
                        this.$refs["error"].display(error.errorCode, error.errorMessage, error.severity);
                    }
                    this.$emitter.emit('stop-loading','update-consent');
                    }.bind(this)).catch(function(error) {                    
                        if (error.response.data.messages.length > 0) {
                            var err = error.response.data.messages[0];
                            this.$refs["error"].display(err.errorCode, err.errorMessage, err.errorType);
                        }
                        this.$emitter.emit('stop-loading','update-consent');
                    }.bind(this));
                }
            },

            cancel: function() {  
                if (this.isPreAuth) {
                    this.$emitter.emit('navigate', 'login');
                }
                else{ 
                    this.fetchData(); 
                    this.$emitter.emit('navigate', 'profileSettings');
                }
            },

            fetchData: function() {     
                var API = "/BFWeb/cwapi/" + this.getClient() + "/comweb/consent?t=" + new Date().getTime();
                this.$axios.get(API).then(function(response) {
                    this.setResponseData(response.data.data);
                }.bind(this));
            },

            setResponseData: function(responseData) {
                this.originalEmail = new String(responseData.customerEmailAddress).valueOf();
                this.originalConsentInfo = [];
                responseData.consentInfo.forEach(function(object) {
                    this.originalConsentInfo.push(new String(object.electronicDeliveryCode).valueOf());
                }.bind(this));
                this.consentData = responseData;
            },
        },

        watch: {
            'consentData.consentInfo': {
                deep: true,
                handler: function(val) {
                    var anyEmail = false;
                    var matchesOriginal = true;

                    val.forEach(function(option, index) {
                        if (option.electronicDeliveryCode === "Y") {
                            anyEmail = true;
                        }

                        if (option.electronicDeliveryCode !== this.originalConsentInfo[index]) {
                            matchesOriginal = false;
                        }
                    }.bind(this));

                    this.consentInfoChanged = !matchesOriginal;

                    this.anyEmail = anyEmail;
                }
            },

            'consentData.customerEmailAddress': function(val) {
                this.emailChanged = val !== this.originalEmail;
            },

            anyEmail: function(val) {
                this.enableUpdateButton(val && this.termsAggreed || !val);
            },

            termsAggreed: function(val) {
                this.enableUpdateButton(val && this.anyEmail || !this.anyEmail);
            },

            anyChanged: function(val) {
                this.enableUpdateButton(val && (this.anyEmail && this.termsAggreed || !this.anyEmail));
            },

            emailInError: function(val) {
                this.enableUpdateButton(!val && (this.anyEmail && this.termsAggreed || !this.anyEmail));
            }
        },
        
        computed: {
            allChanged: function() {
                return this.emailChanged && this.consentInfoChanged;
            },

            anyChanged: function() {
                return this.emailChanged || this.consentInfoChanged;
            },

            hasEmailAddress: function() {
                return this.emailChanged && !this.emailInError || this.originalEmail !== "";
            }
        },

        created: function() {
            this.$emitter.on('retrieve-consent', this.fetchData);
        },

        unmounted: function() {
            this.$emitter.off('retrieve-consent', this.fetchData);
        },

        inject: [
            'getClient'
        ]
    }
</script>